import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Tabs, TabsList, TabsTrigger } from '../ui/tabs';
import { ChevronDown, Search, Upload, Trash2, Loader2, AlertTriangle, Star, Sparkles, Replace, BookmarkPlus, Info } from 'lucide-react';
import { toast } from 'sonner';
import { ensureFont, fontFailed, loadUserFonts, getUserFonts, getUserFontFamilies, uploadUserFont, deleteUserFont, onFontsChanged, twinInUse } from '../../lib/fontLoader';
import { loadFontFavorites, getFontFavorites, isFontFavorite, toggleFontFavorite, onFavoritesChanged } from '../../lib/fontFavorites';
import { fontPairs, pairRole } from '../../lib/fontPairs';
import { apiErrorMessage } from '../../lib/projectApi';
import { useEditor } from '../../context/EditorContext';
import { isEditingText, hasTextSelection } from '../../lib/editingStore';
import { OFFICE_FONTS } from '../../lib/fontTwins';

const FONT_CATEGORIES = {
  'sans-serif': [
    'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins', 'Nunito',
    'Work Sans', 'Raleway', 'Outfit', 'DM Sans', 'Manrope', 'Rubik', 'Karla',
    'Source Sans Pro', 'Fira Sans', 'Cabin', 'Hind', 'Mulish', 'Quicksand',
    'Noto Sans', 'PT Sans', 'Ubuntu', 'Exo 2', 'Josefin Sans', 'Varela Round',
    'Barlow', 'Archivo', 'IBM Plex Sans', 'Space Grotesk', 'Figtree', 'Lexend'
  ],
  'serif': [
    'Playfair Display', 'Merriweather', 'Lora', 'PT Serif', 'Libre Baskerville',
    'Crimson Text', 'EB Garamond', 'Cormorant Garamond', 'Spectral', 'Bitter',
    'Vollkorn', 'Source Serif Pro', 'Noto Serif', 'Amiri', 'Cardo',
    'Old Standard TT', 'Neuton', 'Sorts Mill Goudy', 'Fanwood Text', 'Petrona'
  ],
  'display': [
    'Bebas Neue', 'Righteous', 'Pacifico', 'Lobster', 'Permanent Marker',
    'Alfa Slab One', 'Bungee', 'Staatliches', 'Russo One', 'Bowlby One SC',
    'Bangers', 'Abril Fatface', 'Passion One', 'Fredoka One', 'Titan One',
    'Rammetto One', 'Ultra', 'Shrikhand', 'Sigmar One', 'Fugaz One',
    'Changa One', 'Squada One', 'Audiowide', 'Monoton', 'Rubik Moonrocks'
  ],
  'script': [
    'Dancing Script', 'Great Vibes', 'Parisienne', 'Allura', 'Sacramento',
    'Alex Brush', 'Satisfy', 'Tangerine', 'Pinyon Script', 'Marck Script',
    'Rouge Script', 'Italianno', 'Yellowtail', 'Kaushan Script', 'Cookie',
    'Mr De Haviland', 'Herr Von Muellerhoff', 'Qwigley', 'Berkshire Swash',
    'Niconne', 'Courgette', 'Merienda', 'Caveat', 'La Belle Aurore', 'Homemade Apple'
  ],
  'monospace': [
    'Roboto Mono', 'Fira Code', 'Source Code Pro', 'JetBrains Mono', 'IBM Plex Mono',
    'Space Mono', 'Ubuntu Mono', 'Inconsolata', 'Cousine', 'Anonymous Pro',
    'Overpass Mono', 'DM Mono', 'Azeret Mono', 'Share Tech Mono', 'Red Hat Mono'
  ],
  'decorative': [
    'Orbitron', 'Press Start 2P', 'VT323', 'Creepster', 'Nosifer', 'Butcherman',
    'Metal Mania', 'Rubik Vinyl', 'Henny Penny', 'Flavors', 'Ewert', 'Faster One',
    'Freckle Face', 'Jolly Lodger', 'Kranky', 'Lacquer', 'Luckiest Guy', 'Modak',
    'Nabla', 'Rye', 'Sarina', 'Special Elite', 'Train One', 'Vampiro One'
  ],
  'handwriting': [
    'Indie Flower', 'Architects Daughter', 'Shadows Into Light', 'Patrick Hand',
    'Gochi Hand', 'Gloria Hallelujah', 'Just Another Hand', 'Schoolbell', 
    'Covered By Your Grace', 'Reenie Beanie', 'Rock Salt', 'Neucha', 'Coming Soon', 
    'Short Stack', 'Swanky and Moo Moo', 'Handlee', 'Mali', 'Sedgwick Ave', 
    'Nothing You Could Do', 'Sue Ellen Francisco'
  ],
  'condensed': [
    'Oswald', 'Roboto Condensed', 'Barlow Condensed', 'Pathway Gothic One',
    'Yanone Kaffeesatz', 'Anton', 'Saira Condensed', 'Fjalla One', 'Chivo',
    'Economica', 'Encode Sans Condensed', 'IBM Plex Sans Condensed', 'Teko', 
    'Pathway Extreme', 'Jost', 'Six Caps', 'Chakra Petch', 'Dosis', 'Graduate'
  ]
};

/** A–Z, case-insensitive and accent-aware ("Exo 2" before "Fira"). Favourites and uploads are sorted the same way. */
export const sortFonts = (fonts) => [...fonts].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
Object.keys(FONT_CATEGORIES).forEach((k) => { FONT_CATEGORIES[k] = sortFonts(FONT_CATEGORIES[k]); });
const ALL_FONTS = sortFonts([...Object.values(FONT_CATEGORIES).flat(), ...OFFICE_FONTS]); // Office names included so a search for "Calibri" finds it
const FONT_FILE_TYPES = '.ttf,.otf,.woff,.woff2';

/** Re-render when the user's uploaded fonts change; returns their family names. */
const useUserFontFamilies = () => {
  const [, bump] = useState(0);
  useEffect(() => { loadUserFonts(); return onFontsChanged(() => bump((n) => n + 1)); }, []);
  return getUserFontFamilies();
};

/** Re-render when the starred fonts change; returns the favourite family names (in the order they were starred). */
const useFontFavorites = () => {
  const [, bump] = useState(0);
  useEffect(() => { loadFontFavorites(); return onFavoritesChanged(() => bump((n) => n + 1)); }, []);
  return getFontFavorites();
};

const testKey = (font) => font.replace(/\s+/g, '-');

/** Star / unstar a family; the favourite list is shared across every publication. */
const starFont = async (font) => {
  try {
    const on = await toggleFontFavorite(font);
    toast.success(on ? `${font} added to Favourites` : `${font} removed from Favourites`, { id: 'font-fav' });
  } catch (e) {
    toast.error(await apiErrorMessage(e, 'Favourites could not be saved.'));
  }
};

/** One "pairs well with" chip: the partner family in its own face; click applies it. */
const PairChip = ({ font, partner, keepFocus, onSelect, onHover }) => {
  const [state, setState] = useState(() => (fontFailed(partner) ? 'failed' : 'idle'));
  useEffect(() => { let c = false; ensureFont(partner).then((ok) => { if (!c) setState(ok ? 'ready' : 'failed'); }); return () => { c = true; }; }, [partner]);
  const role = pairRole(font, partner);
  return (
    <button
      type="button"
      onMouseDown={(e) => { if (keepFocus && e.button === 0) e.preventDefault(); }}
      onClick={() => onSelect(partner)}
      onMouseEnter={() => onHover && onHover(partner)}
      title={`${partner} — good for ${role} next to ${font}`}
      className="max-w-full shrink-0 rounded-full border border-border bg-background px-2.5 py-0.5 text-[13px] leading-5 text-foreground hover:border-primary/60 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
      style={{ fontFamily: `"${partner}"` }}
      data-testid={`font-pair-${testKey(partner)}`}
      data-font-state={state}
    >
      <span className="truncate">{partner}</span>
    </button>
  );
};

/**
 * One row of the font list. The family is loaded from Google Fonts the first time the row scrolls into view, so every entry
 * previews in its own face (index.css only preloads a handful).
 */
const FontRow = ({ font, selected, keepFocus, onSelect, onHover, user = false, onDelete, favorite = false }) => {
  const ref = useRef(null);
  const [state, setState] = useState(() => (fontFailed(font) ? 'failed' : 'idle'));
  useEffect(() => {
    const el = ref.current;
    if (!el || user) return undefined;
    let cancelled = false;
    const load = () => ensureFont(font).then((ok) => { if (!cancelled) setState(ok ? 'ready' : 'failed'); });
    if (typeof IntersectionObserver === 'undefined') { load(); return () => { cancelled = true; }; }
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); } }, { root: el.closest('[data-font-list]') || el.closest('[data-radix-scroll-area-viewport]') || null, rootMargin: '120px' });
    io.observe(el);
    return () => { cancelled = true; io.disconnect(); };
  }, [font, user]);
  return (
    <div ref={ref} className={`group flex items-center rounded ${selected ? 'bg-primary/20 text-primary' : 'hover:bg-accent'}`} onMouseEnter={() => onHover && onHover(font)} data-testid={`font-row-${testKey(font)}`}>
      <button
        type="button"
        className="flex-1 min-w-0 text-left px-3 py-2 text-foreground"
        onMouseDown={(e) => { if (keepFocus && e.button === 0) e.preventDefault(); }}
        onClick={() => onSelect(font)}
        title={state === 'failed' ? `${font} is not available from Google Fonts — upload it under My fonts` : font}
        data-testid={`font-option-${font.replace(/\s+/g, '-')}`}
        data-font-state={state}
      >
        <span className="font-preview text-lg truncate block" style={{ fontFamily: `"${font}"` }}>{font}</span>
      </button>
      {state === 'failed' && <AlertTriangle className="h-3.5 w-3.5 text-amber-500 mr-1 shrink-0" aria-label="Not available" />}
      <button
        type="button"
        onMouseDown={(e) => { if (keepFocus && e.button === 0) e.preventDefault(); }}
        onClick={(e) => { e.stopPropagation(); starFont(font); }}
        className={`mr-1 p-1 rounded shrink-0 focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-opacity ${favorite ? 'opacity-100 text-amber-500 hover:text-amber-600' : 'opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-amber-500'}`}
        title={favorite ? `Remove ${font} from Favourites` : `Add ${font} to Favourites`}
        aria-pressed={favorite}
        aria-label={favorite ? `Remove ${font} from favourites` : `Add ${font} to favourites`}
        data-testid={`font-fav-toggle-${testKey(font)}`}
      >
        <Star className="h-3.5 w-3.5" fill={favorite ? 'currentColor' : 'none'} />
      </button>
      {user && onDelete && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(font); }} className="mr-1.5 p-1 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title={`Remove ${font}`} data-testid={`font-delete-${font.replace(/\s+/g, '-')}`}><Trash2 className="h-3.5 w-3.5" /></button>
      )}
    </div>
  );
};

/**
 * Font family picker (ribbon `compact` combo or full-width button). `onPreview(family | null)` — optional live preview:
 * hovering a font (after an 80 ms settle, once its face is loaded) calls it with the family; leaving the list, closing the
 * menu or picking a font calls it with null. The document is never touched by the preview — only the caller decides what
 * to do with it (the ribbons show it on the selected text boxes).
 */
export const FontPicker = ({ value, onChange, onPreview, compact = false, testId = 'font-picker-trigger', allowSaveScheme = true }) => {
  const [open, setOpen] = useState(false);
  // Never steal the caret from a text box being edited (the ribbon combo always, the Properties-pane button while
  // editing): the pick must land on the highlighted text / caret word, not on the whole box.
  const keepFocus = compact || isEditingText();
  const editingInline = isEditingText() && hasTextSelection();
  const previewRef = useRef({ timer: null, font: null, active: null });
  const onPreviewRef = useRef(onPreview);
  onPreviewRef.current = onPreview;
  const emitPreview = useCallback((font) => {
    const pr = previewRef.current;
    if (pr.active === font) return;
    pr.active = font;
    if (onPreviewRef.current) onPreviewRef.current(font);
  }, []);
  const hoverFont = useCallback((font) => {
    if (!onPreviewRef.current) return;
    const pr = previewRef.current;
    pr.font = font;
    if (pr.timer) clearTimeout(pr.timer);
    pr.timer = setTimeout(() => {
      pr.timer = null;
      if (pr.font !== font) return;
      // load the face first so the preview shows the real font, not a fallback
      ensureFont(font).then(() => { if (pr.font === font) emitPreview(font); });
    }, 80);
  }, [emitPreview]);
  const endPreview = useCallback(() => {
    const pr = previewRef.current;
    pr.font = null;
    if (pr.timer) { clearTimeout(pr.timer); pr.timer = null; }
    emitPreview(null);
  }, [emitPreview]);
  useEffect(() => { if (!open) endPreview(); }, [open, endPreview]);
  useEffect(() => () => endPreview(), [endPreview]);
  const [search, setSearch] = useState('');
  // a fresh search box every time the picker opens (a stale filter from last time hid the whole list)
  useEffect(() => { if (open) setSearch(''); }, [open]);
  const [category, setCategory] = useState('all');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const userFamilies = useUserFontFamilies();
  const favorites = useFontFavorites();
  const { openDialog } = useEditor();

  // The current value previews in its own face too (documents may use fonts the picker never showed)
  const [valueMissing, setValueMissing] = useState(false);
  const [valueTwin, setValueTwin] = useState(null); // { name, twin, metric } when a Microsoft font is shown through its free twin
  useEffect(() => {
    if (!value) { setValueMissing(false); setValueTwin(null); return undefined; }
    let cancelled = false;
    ensureFont(value).then((ok) => { if (!cancelled) { setValueMissing(!ok); setValueTwin(ok ? twinInUse(value) : null); } });
    return () => { cancelled = true; };
  }, [value]);

  const pairs = useMemo(() => (value ? fontPairs(value, 4) : []), [value]);

  const filteredFonts = useMemo(() => {
    const lower = (f) => f.toLowerCase();
    const favs = favorites.filter((f) => f);
    let fonts;
    if (category === 'all') {
      // pinned on top: favourites, then the user's own uploads — each A–Z — then the whole catalogue A–Z
      const seen = new Set();
      fonts = [...sortFonts(favs), ...sortFonts(userFamilies), ...ALL_FONTS].filter((f) => { const k = lower(f); if (seen.has(k)) return false; seen.add(k); return true; });
    } else if (category === 'favorites') fonts = sortFonts(favs);
    else if (category === 'user') fonts = sortFonts(userFamilies);
    else if (category === 'office') fonts = OFFICE_FONTS;
    else fonts = FONT_CATEGORIES[category] || [];
    if (search) fonts = fonts.filter((font) => lower(font).includes(search.toLowerCase()));
    return fonts;
  }, [category, search, userFamilies, favorites]);

  const handleSelect = (font) => {
    endPreview();
    onChange(font);
    setOpen(false);
  };

  /** Upload one or more font files (TTF / OTF / WOFF / WOFF2); the family name is read from the font itself. */
  const uploadFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setUploading(true);
    const added = [];
    for (const file of list) {
      try {
        const f = await uploadUserFont(file);
        added.push(`${f.family}${f.style && f.style !== 'Regular' ? ` ${f.style}` : ''}`);
      } catch (e) {
        toast.error(`${file.name}: ${await apiErrorMessage(e, 'could not be uploaded.')}`);
      }
    }
    setUploading(false);
    if (added.length) {
      toast.success(`${added.length === 1 ? 'Font added' : `${added.length} fonts added`}: ${added.join(', ')}`, { description: 'Available in every publication and used automatically by imported Publisher files that name it.' });
      setCategory('user');
    }
  };

  const removeFamily = async (family) => {
    const fonts = getUserFonts().filter((f) => f.family === family);
    try {
      for (const f of fonts) await deleteUserFont(f.id);
      toast.success(`${family} removed`);
    } catch (e) {
      toast.error(await apiErrorMessage(e, 'The font could not be removed.'));
    }
  };
  
  return (
    <Popover open={open} onOpenChange={setOpen} modal={false}>
      <PopoverTrigger asChild>
        {compact ? (
          <button
            type="button"
            onMouseDown={(e) => { if (e.button === 0) e.preventDefault(); }}
            className="h-[22px] w-[132px] flex items-center justify-between rounded-sm border border-input bg-background px-1.5 text-[12px] text-foreground hover:bg-[hsl(var(--wp-command-hover))] focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            title="Font"
            data-testid={testId}
          >
            <span className="font-preview truncate" style={{ fontFamily: value ? `"${value}"` : undefined }}>{value || 'Font'}</span>
            <ChevronDown className="ml-1 h-3 w-3 shrink-0 opacity-60" />
          </button>
        ) : (
          <Button
            variant="outline"
            className="w-full justify-between bg-background border-border hover:bg-accent text-foreground"
            onMouseDown={(e) => { if (keepFocus && e.button === 0) e.preventDefault(); }}
            data-testid={testId}
          >
            <span 
              className="font-preview truncate"
              style={{ fontFamily: value ? `"${value}"` : undefined }}
            >
              {value}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 bg-popover border-border z-[90] flex flex-col max-h-[var(--radix-popover-content-available-height)]"
        align="start"
        collisionPadding={8}
        data-ribbon
        data-keep-editing
        onOpenAutoFocus={(e) => { if (keepFocus) e.preventDefault(); }}
      >
        <div className="p-3 border-b border-border shrink-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search fonts..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-background border-border"
              data-testid="font-search-input"
            />
          </div>
          {onPreview && <p className="mt-1.5 text-[10.5px] leading-tight text-muted-foreground" data-testid="font-preview-hint">{editingInline ? 'Click a font to apply it to the highlighted text only' : isEditingText() ? 'Click a font to apply it to the word at the cursor, or to what you type next' : 'Hover a font to preview it on the selected text · click to apply'}</p>}
        </div>

        {value && !valueMissing && valueTwin && (
          <div className="mx-3 mt-2 shrink-0 flex items-start gap-2 rounded-md border border-border bg-muted/50 px-2 py-1.5 text-[11px] text-muted-foreground" data-testid="font-twin-notice">
            <Info className="h-3.5 w-3.5 shrink-0 mt-px" />
            <span className="flex-1 min-w-0 leading-snug"><span className="font-medium text-foreground">{valueTwin.name}</span> is not installed here — shown with its free twin <span className="font-medium text-foreground">{valueTwin.twin}</span>{valueTwin.metric ? ' (same letter widths, so lines break the same)' : ' (closest look-alike)'}.</span>
          </div>
        )}
        {value && valueMissing && (
          <div className="mx-3 mt-2 shrink-0 flex items-start gap-2 rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 px-2 py-1.5 text-[11px] text-amber-900 dark:text-amber-100" data-testid="font-missing-notice">
            <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 min-w-0 leading-snug"><span className="font-medium">{value}</span> is not available here — a substitute face is shown.</span>
            <button type="button" onMouseDown={(e) => { if (keepFocus && e.button === 0) e.preventDefault(); }} onClick={() => { setOpen(false); openDialog('font-substitution', { fonts: [value] }); }} className="inline-flex items-center gap-1 rounded-sm border border-amber-500/40 bg-background px-1.5 py-0.5 font-medium hover:bg-amber-100 dark:hover:bg-amber-900/40" data-testid="font-missing-replace-button"><Replace className="h-3 w-3" /> Replace…</button>
          </div>
        )}

        {value && pairs.length > 0 && !search && (
          <div className="px-3 pt-2 pb-2.5 border-b border-border shrink-0" data-testid="font-pairs-strip">
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground mb-1.5">
              <Sparkles className="h-3 w-3" /> Pairs well with <span className="font-medium text-foreground truncate" style={{ fontFamily: `"${value}"` }}>{value}</span>
              {allowSaveScheme && (
              <button
                type="button"
                onMouseDown={(e) => { if (keepFocus && e.button === 0) e.preventDefault(); }}
                onClick={() => { const partner = pairs[0]; const headingFirst = pairRole(value, partner) !== 'headings'; setOpen(false); openDialog('font-scheme', headingFirst ? { heading: value, body: partner } : { heading: partner, body: value }); }}
                className="ml-auto inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                title="Save this font and its partner as a named font scheme (Page Design ▸ Schemes ▸ Fonts)"
                data-testid="font-pairs-save-scheme"
              >
                <BookmarkPlus className="h-3 w-3" /> Save pairing…
              </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5" onMouseLeave={endPreview}>
              {pairs.map((partner) => <PairChip key={partner} font={value} partner={partner} keepFocus={keepFocus} onSelect={handleSelect} onHover={hoverFont} />)}
            </div>
          </div>
        )}

        <Tabs value={category} onValueChange={setCategory} className="w-full flex-1 min-h-0 flex flex-col">
          <div className="border-b border-border shrink-0">
            <TabsList className="h-auto p-1 bg-transparent w-full flex flex-wrap justify-start gap-0.5" data-testid="font-category-tabs">
              <TabsTrigger 
                value="all" 
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
              >
                All
              </TabsTrigger>
              <TabsTrigger
                value="favorites"
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground inline-flex items-center gap-1"
                data-testid="font-tab-favorites"
              >
                <Star className="h-3 w-3" fill={favorites.length ? 'currentColor' : 'none'} /> Favourites{favorites.length ? ` (${favorites.length})` : ''}
              </TabsTrigger>
              <TabsTrigger 
                value="sans-serif" 
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
              >
                Sans
              </TabsTrigger>
              <TabsTrigger 
                value="serif" 
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
              >
                Serif
              </TabsTrigger>
              <TabsTrigger 
                value="display" 
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
              >
                Display
              </TabsTrigger>
              <TabsTrigger 
                value="script" 
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
              >
                Script
              </TabsTrigger>
              <TabsTrigger 
                value="monospace" 
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
              >
                Mono
              </TabsTrigger>
              <TabsTrigger 
                value="handwriting" 
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
              >
                Hand
              </TabsTrigger>
              <TabsTrigger
                value="office"
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
                data-testid="font-tab-office"
                title="The Windows / Office fonts Publisher files use — shown with their free look-alikes where they are not installed"
              >
                Office
              </TabsTrigger>
              <TabsTrigger
                value="user"
                className="text-xs data-[state=active]:bg-accent data-[state=active]:text-accent-foreground px-2 py-1 text-muted-foreground"
                data-testid="font-tab-user"
              >
                My fonts{userFamilies.length ? ` (${userFamilies.length})` : ''}
              </TabsTrigger>
            </TabsList>
          </div>

          {category === 'user' && (
            <div className="px-3 py-2 border-b border-border flex items-center gap-2" data-testid="font-user-toolbar">
              <input ref={fileRef} type="file" accept={FONT_FILE_TYPES} multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ''; }} data-testid="font-upload-input" />
              <Button size="sm" variant="outline" className="h-7 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="font-upload-button">
                {uploading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Upload className="h-3.5 w-3.5 mr-1.5" />} Add font…
              </Button>
              <span className="text-[11px] text-muted-foreground leading-tight">TTF, OTF, WOFF or WOFF2 · saved with your account · Publisher imports use them automatically</span>
            </div>
          )}

          {/* Native scroll container: it sizes itself to the room the popover leaves (max-height = available height) and
              scrolls its own overflow — a Radix ScrollArea needs a definite height here and ended up unscrollable. */}
          <div className="flex-1 min-h-[120px] overflow-y-auto overscroll-contain" data-font-list role="listbox" aria-label="Fonts" data-testid="font-list" data-preview={onPreview ? 'live' : undefined} onMouseLeave={endPreview}>
            <div className="p-2 space-y-1">
              {filteredFonts.map((font) => (
                <FontRow key={font} font={font} selected={value === font} keepFocus={keepFocus} onSelect={handleSelect} onHover={hoverFont} user={userFamilies.includes(font)} onDelete={category === 'user' ? removeFamily : undefined} favorite={isFontFavorite(font)} />
              ))}
              {filteredFonts.length === 0 && category === 'favorites' && !search && (
                <div className="text-center text-muted-foreground py-6 px-4 text-sm space-y-2" data-testid="font-favorites-empty">
                  <p className="flex items-center justify-center gap-1"><Star className="h-4 w-4" /> No favourite fonts yet.</p>
                  <p className="text-xs">Hover a font and click its star — favourites are kept with your account and listed first in every publication.</p>
                </div>
              )}
              {filteredFonts.length === 0 && category === 'user' && !search && (
                <div className="text-center text-muted-foreground py-6 px-4 text-sm space-y-2" data-testid="font-user-empty">
                  <p>No fonts of your own yet.</p>
                  <p className="text-xs">Add the fonts a Publisher file was designed with (for example from your Windows Fonts folder) and the import will render with them.</p>
                </div>
              )}
              {filteredFonts.length === 0 && (!['user', 'favorites'].includes(category) || search) && (
                <p className="text-center text-muted-foreground py-4">No fonts found</p>
              )}
            </div>
          </div>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
};
